/**
 * Política BLOQUEA_ART_DISCONTINUADOS (Configuraciones: sección VENTAS|COMPRAS, campo BLOQUEA_ART_DISCONTINUADOS).
 *
 * Contrato que el frontend envía al API (camelCase, case-insensitive):
 * - seccionDocumento: "VENTAS" | "COMPRAS"
 * - modoBloqueoArtDiscontinuados: 0 | 1 | 2  (espejo del parámetro; el backend debe leer Configuraciones)
 * - excluirInactivos: solo tipo INA
 * - excluirDiscontinuados: false en documentos (modo 1 lista DIS pero no permite seleccionarlos)
 * - soloOperativo: true = rechazar INA (409). DIS se decide con modoBloqueoArtDiscontinuados.
 *
 * Modo 0 = permite DIS · 1 = muestra DIS no seleccionables · 2 = advierte al agregar.
 */
import configuracionService from '../services/configuracionService';
import productoService, {
  type Producto,
  type ProductoSearchParams,
} from '../services/productoService';

export type SeccionBloqueoArtDiscontinuados = 'VENTAS' | 'COMPRAS';

/**
 * 0 = permite discontinuados
 * 1 = muestra discontinuados pero no se pueden agregar
 * 2 = advierte y pregunta al agregar
 */
export type ModoBloqueoArtDiscontinuados = 0 | 1 | 2;

export const CAMPO_BLOQUEA_ART_DISCONTINUADOS = 'BLOQUEA_ART_DISCONTINUADOS';

const cacheModo: Partial<Record<SeccionBloqueoArtDiscontinuados, ModoBloqueoArtDiscontinuados>> = {};

/** Códigos DIS ya confirmados en esta sesión de navegación (evita preguntar dos veces). */
const confirmadosDiscontinuadosSesion = new Set<string>();

/** Códigos DIS rechazados en esta sesión (evita reabrir el confirm en bucles de blur). */
const rechazadosDiscontinuadosSesion = new Set<string>();

/** Evita confirm concurrentes (doble blur / reentrada). */
let confirmDiscontinuadoEnCurso = false;

function keyConfirmado(seccion: SeccionBloqueoArtDiscontinuados, codigo: string): string {
  return `${seccion}|${codigo.trim().toUpperCase()}`;
}

export function esTipoDiscontinuado(tipoArticulo?: string | null): boolean {
  return String(tipoArticulo ?? '').trim().toUpperCase() === 'DIS';
}

/** Solo INA (Inactivo). DIS se gobierna por el parámetro. */
export function esTipoInactivo(tipoArticulo?: string | null): boolean {
  return String(tipoArticulo ?? '').trim().toUpperCase() === 'INA';
}

export function parseModoBloqueoArtDiscontinuados(raw: string | null | undefined): ModoBloqueoArtDiscontinuados {
  const v = String(raw ?? '').trim();
  const digit = v.match(/^[012]/)?.[0];
  if (digit === '0') return 0;
  if (digit === '2') return 2;
  if (digit === '1') return 1;
  // Ausente o inválido: bloquear (conservador).
  return 1;
}

async function leerConfigBloqueo(seccion: SeccionBloqueoArtDiscontinuados) {
  const pick = (items: { campo?: string; usuario?: string; valor?: string | null }[]) => {
    const exactCampo = items.filter(
      (c) => String(c.campo ?? '').trim().toUpperCase() === CAMPO_BLOQUEA_ART_DISCONTINUADOS,
    );
    const deSeccion = exactCampo.filter((c) => String((c as { seccion?: string }).seccion ?? '').trim().toUpperCase() === seccion);
    const pool = deSeccion.length ? deSeccion : exactCampo;
    const global = pool.find((c) => !String(c.usuario ?? '').trim());
    return global ?? pool[0] ?? null;
  };

  const byKey = await configuracionService.getByKey(seccion, CAMPO_BLOQUEA_ART_DISCONTINUADOS);
  if (byKey && byKey.valor != null && String(byKey.valor).trim() !== '') {
    return byKey;
  }

  const pageSeccion = await configuracionService.getConfiguraciones({
    seccion,
    campo: CAMPO_BLOQUEA_ART_DISCONTINUADOS,
    pageNumber: 1,
    pageSize: 50,
  });
  const fromSeccion = pick(pageSeccion.items ?? []);
  if (fromSeccion) return fromSeccion;

  const pageCampo = await configuracionService.getConfiguraciones({
    campo: CAMPO_BLOQUEA_ART_DISCONTINUADOS,
    searchTerm: CAMPO_BLOQUEA_ART_DISCONTINUADOS,
    pageNumber: 1,
    pageSize: 50,
  });
  return pick(pageCampo.items ?? []) ?? byKey;
}

export async function getModoBloqueoArtDiscontinuados(
  seccion: SeccionBloqueoArtDiscontinuados,
  options?: { force?: boolean },
): Promise<ModoBloqueoArtDiscontinuados> {
  if (!options?.force && cacheModo[seccion] != null) {
    return cacheModo[seccion]!;
  }
  try {
    const config = await leerConfigBloqueo(seccion);
    const modo = parseModoBloqueoArtDiscontinuados(config?.valor);
    cacheModo[seccion] = modo;
    return modo;
  } catch {
    cacheModo[seccion] = 1;
    return 1;
  }
}

/** Invalida caché (p. ej. tras editar parámetros en la misma sesión). */
export function clearCacheBloqueoArtDiscontinuados(): void {
  delete cacheModo.VENTAS;
  delete cacheModo.COMPRAS;
  confirmadosDiscontinuadosSesion.clear();
  rechazadosDiscontinuadosSesion.clear();
}

/** Permite volver a preguntar por un DIS tras editar el código en el formulario. */
export function clearRechazoDiscontinuado(
  seccion: SeccionBloqueoArtDiscontinuados,
  codigo?: string | null,
): void {
  const cod = String(codigo ?? '').trim();
  if (!cod) return;
  rechazadosDiscontinuadosSesion.delete(keyConfirmado(seccion, cod));
}

export function mensajeArticuloInactivo(codigo?: string): string {
  const cod = String(codigo ?? '').trim();
  return cod
    ? `El artículo "${cod}" está inactivo y no puede utilizarse.`
    : 'El artículo está inactivo y no puede utilizarse.';
}

export function mensajeArticuloDiscontinuadoBloqueado(codigo?: string): string {
  const cod = String(codigo ?? '').trim();
  return cod
    ? `El artículo "${cod}" está discontinuado y no puede utilizarse.`
    : 'El artículo está discontinuado y no puede utilizarse.';
}

export function mensajeConfirmarArticuloDiscontinuado(codigo?: string, descripcion?: string): string {
  const cod = String(codigo ?? '').trim();
  const desc = String(descripcion ?? '').trim();
  const label = [cod, desc].filter(Boolean).join(' — ') || 'este artículo';
  return `El artículo ${label} está discontinuado. ¿Desea continuar de todas formas?`;
}

/** Modo 1: DIS se muestra en listados pero no se puede agregar al documento. */
export function esDiscontinuadoBloqueadoParaAgregar(
  tipoArticulo: string | null | undefined,
  modo: ModoBloqueoArtDiscontinuados,
): boolean {
  return modo === 1 && esTipoDiscontinuado(tipoArticulo);
}

export type ResultadoUsoProductoDocumento =
  | { permitido: true }
  | { permitido: false; motivo: 'inactivo' | 'discontinuado_bloqueado' | 'discontinuado_rechazado'; mensaje: string };

export type ConfirmarDiscontinuadoFn = (mensaje: string) => boolean | Promise<boolean>;

/**
 * Evalúa si un producto (por tipo) puede usarse en un documento de ventas/compras
 * según BLOQUEA_ART_DISCONTINUADOS. INA siempre se rechaza.
 */
export async function evaluarUsoProductoDocumento(
  producto: { codigoArticulo?: string | null; tipoArticulo?: string | null; descripcion?: string | null },
  seccion: SeccionBloqueoArtDiscontinuados,
  options?: { confirmar?: ConfirmarDiscontinuadoFn },
): Promise<ResultadoUsoProductoDocumento> {
  const codigo = String(producto.codigoArticulo ?? '').trim();
  const tipo = producto.tipoArticulo;

  if (esTipoInactivo(tipo)) {
    return { permitido: false, motivo: 'inactivo', mensaje: mensajeArticuloInactivo(codigo) };
  }

  if (!esTipoDiscontinuado(tipo)) {
    return { permitido: true };
  }

  const modo = await getModoBloqueoArtDiscontinuados(seccion);
  if (modo === 0) {
    return { permitido: true };
  }
  if (modo === 1) {
    return {
      permitido: false,
      motivo: 'discontinuado_bloqueado',
      mensaje: mensajeArticuloDiscontinuadoBloqueado(codigo),
    };
  }

  if (codigo && confirmadosDiscontinuadosSesion.has(keyConfirmado(seccion, codigo))) {
    return { permitido: true };
  }

  if (codigo && rechazadosDiscontinuadosSesion.has(keyConfirmado(seccion, codigo))) {
    return {
      permitido: false,
      motivo: 'discontinuado_rechazado',
      mensaje: 'No se agregó el artículo discontinuado.',
    };
  }

  if (confirmDiscontinuadoEnCurso) {
    return {
      permitido: false,
      motivo: 'discontinuado_rechazado',
      mensaje: 'No se agregó el artículo discontinuado.',
    };
  }

  const confirmar = options?.confirmar ?? ((msg: string) => window.confirm(msg));
  confirmDiscontinuadoEnCurso = true;
  let ok = false;
  try {
    ok = await confirmar(
      mensajeConfirmarArticuloDiscontinuado(codigo, producto.descripcion ?? undefined),
    );
  } finally {
    confirmDiscontinuadoEnCurso = false;
  }
  if (!ok) {
    if (codigo) {
      rechazadosDiscontinuadosSesion.add(keyConfirmado(seccion, codigo));
    }
    return {
      permitido: false,
      motivo: 'discontinuado_rechazado',
      mensaje: 'No se agregó el artículo discontinuado.',
    };
  }
  if (codigo) {
    rechazadosDiscontinuadosSesion.delete(keyConfirmado(seccion, codigo));
    confirmadosDiscontinuadosSesion.add(keyConfirmado(seccion, codigo));
  }
  return { permitido: true };
}

/** Query/body para GET producto y resolver código de barras en documentos. */
export async function opcionesGetProductoDocumento(
  seccion: SeccionBloqueoArtDiscontinuados,
): Promise<{
  soloOperativo: boolean;
  modoBloqueoArtDiscontinuados: ModoBloqueoArtDiscontinuados;
  seccionDocumento: SeccionBloqueoArtDiscontinuados;
}> {
  const modo = await getModoBloqueoArtDiscontinuados(seccion);
  return {
    // INA siempre se rechaza; DIS lo decide modoBloqueoArtDiscontinuados (0/1/2).
    soloOperativo: true,
    modoBloqueoArtDiscontinuados: modo,
    seccionDocumento: seccion,
  };
}

/**
 * Carga el producto con los query params de documento, sin pedir confirmación DIS.
 * Usa el modo real de config (default 1). En modo 1 el API puede responder 409 para DIS;
 * la política UX (listar / confirmar) se aplica en el caller con evaluarUso o al capturar el 409.
 */
export async function cargarProductoParaDocumento(
  codigoArticulo: string,
  seccion: SeccionBloqueoArtDiscontinuados,
): Promise<Producto> {
  const codigo = String(codigoArticulo ?? '').trim();
  if (!codigo) {
    throw new Error('Debe indicar un código de artículo');
  }
  const modo = await getModoBloqueoArtDiscontinuados(seccion);
  return productoService.getProducto(codigo, {
    soloOperativo: true,
    modoBloqueoArtDiscontinuados: modo,
    seccionDocumento: seccion,
  });
}

/**
 * Obtiene el producto y aplica la política de discontinuados / inactivos.
 * Lanza Error con mensaje listo para snackbar si no está permitido.
 * La advertencia del modo 2 ocurre acá (al usar/agregar), no al listar resultados.
 */
export async function obtenerProductoParaDocumento(
  codigoArticulo: string,
  seccion: SeccionBloqueoArtDiscontinuados,
  options?: { confirmar?: ConfirmarDiscontinuadoFn },
): Promise<Producto> {
  const producto = await cargarProductoParaDocumento(codigoArticulo, seccion);
  const decision = await evaluarUsoProductoDocumento(producto, seccion, options);
  if (!decision.permitido) {
    throw new Error(decision.mensaje);
  }
  return producto;
}

/**
 * Params de búsqueda para documentos.
 * Siempre lista DIS (excluirDiscontinuados=false); modo 1 los muestra no seleccionables en UI.
 * excluirInactivos oculta solo INA.
 */
export async function paramsBusquedaProductoDocumento(
  seccion: SeccionBloqueoArtDiscontinuados,
): Promise<
  Pick<
    ProductoSearchParams,
    'excluirInactivos' | 'excluirDiscontinuados' | 'modoBloqueoArtDiscontinuados' | 'seccionDocumento'
  >
> {
  const modo = await getModoBloqueoArtDiscontinuados(seccion);
  return {
    excluirInactivos: true,
    excluirDiscontinuados: false,
    modoBloqueoArtDiscontinuados: modo,
    seccionDocumento: seccion,
  };
}


/** Quita artículos INA de un listado (DIS se dejan según el parámetro). */
export function filtrarProductosInactivos<T extends { tipoArticulo?: string | null }>(items: T[]): T[] {
  return items.filter((p) => !esTipoInactivo(p.tipoArticulo));
}
